import React, { forwardRef, useCallback, useRef } from 'react';

const Tab = forwardRef(({
  id,
  selected,
  className = '',
  controls = '',
  children,
  value,
  onSelected = () => { },
  ...events
}, ref) => (
  <button
    ref={ref}
    aria-controls={controls}
    aria-selected={selected}
    className={className}
    data-value={value}
    id={id}
    onClick={onSelected}
    role="tab"
    tabIndex={selected ? 0 : -1}
    type="button"
    {...events}
  >
    {children}
  </button>
));

const LEFT_KEY = 'ArrowLeft';
const RIGHT_KEY = 'ArrowRight';

export const TabList = ({
  containerClassName = '',
  tabClassName = '',
  TabContentItem,
  tabList = [],
  tabSelected = '',
  tabIdPrefix = '',
  onTabChanged = () => { },
}) => {
  const buttonsRef = useRef([]);

  const setButtonRef = useCallback((index) => (el) => {
    if (el) {
      buttonsRef.current[index] = el;
    }
  }, []);

  const handleTabSelected = useCallback((id) => () => {
    onTabChanged(id);
  }, [onTabChanged]);

  const handleKeyDown = (event) => {
    const currentIndex = buttonsRef.current.indexOf(document.activeElement);

    if (currentIndex === -1) return;

    if (event.key === RIGHT_KEY) {
      event.preventDefault();
      const nextIndex = (currentIndex + 1) % buttonsRef.current.length;
      buttonsRef.current[nextIndex]?.focus();
    } else if (event.key === LEFT_KEY) {
      event.preventDefault();
      const prevIndex = currentIndex === 0
        ? buttonsRef.current.length - 1
        : currentIndex - 1;
      buttonsRef.current[prevIndex]?.focus();
    }
  };

  return (
    <div className={containerClassName} role="tablist">
      {tabList.map((tab, index) => (
        <Tab
          key={tab.id}
          ref={setButtonRef(index)}
          className={tabClassName}
          controls={tab.controls}
          id={`${tabIdPrefix}-${tab.id}`}
          onFocus={handleTabSelected(tab.id)}
          onKeyDown={handleKeyDown}
          onSelected={handleTabSelected(tab.id)}
          selected={tabSelected === tab.id}
          value={tab.id}
        >
          <TabContentItem info={tab} />
        </Tab>
      ))}
    </div>
  );
};
